import EmployeeSelect from '@/components/Form/Shared/EmployeeSelect'
import SubHeaders from '@/components/Headers/SubHeaders'
import Paper from '@/components/Paper'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import AddIcons from '@/icons/AddIcons'
import { formatPGDate } from '@/utils/function'
import { paths } from '@/utils/path'
import dayjs from 'dayjs'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { FormItem, GridHookForm, Typogaphy } from 'vnetwork-ui'
import { createKPIEmployee, getKPIProductGroups } from '../../services'
import { KPIEmployeeForm, RateConfig } from '../../types'

const Create = () => {
    const navigate = useNavigate()

    const { t } = useTranslation()
    const { data: productGroups } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.PRODUCT_GROUPS_LIST],
        queryFn: getKPIProductGroups,
    })

    const { mutate: CreateMutate, isPending } = useMutationWithHandler(
        {
            mutationFn: createKPIEmployee,
        },
        {
            reactQueryKeys: QUERY_KEYS.KPI,
            successMessage: 'Tạo cấu hình KPI thành công',
            onSuccess: () => {
                navigate(paths.kpiManage)
            },
        }
    )

    const methods = useForm<KPIEmployeeForm>({
        defaultValues: {
            employee_id: [],
            default_rates: {
                rate_of_newsale: 0.05,
                rate_of_upsale: 0.03,
                rate_of_renewal: 0.01,
            },
            effective_date: dayjs(),
            expired_date: dayjs(),
            product_group_rates: productGroups?.results?.reduce(
                (acc: Record<string, RateConfig>, group: string) => {
                    acc[group] = {
                        rate_of_newsale: 0.05,
                        rate_of_upsale: 0.03,
                        rate_of_renewal: 0.01,
                    }
                    return acc
                },
                {}
            ),
        },
    })

    const onSubmit = async (data: KPIEmployeeForm) => {
        //   console.log(data)
        const promises: any[] = []

        data?.employee_id?.forEach((employee) => {
            productGroups?.results?.forEach((group) => {
                const groupRates =
                    data.product_group_rates[group] || data.default_rates
                promises.push(
                    CreateMutate({
                        employee_id: employee,
                        product_group_name: group,
                        effective_date: formatPGDate(
                            data?.effective_date || '',
                            'YYYY-MM-DD'
                        ),
                        expired_date: formatPGDate(
                            data?.expired_date || '',
                            'YYYY-MM-DD'
                        ),
                        ...groupRates,
                    })
                )
            })
        })

        await Promise.all(promises)
    }

    return (
        <div>
            <SubHeaders
                breadcrumbs={[
                    {
                        path: paths.kpiManage,
                        title: t('kpi-manage'),
                    },
                ]}
            />
            <Paper className="mt-2">
                <form onSubmit={methods.handleSubmit(onSubmit)}>
                    <GridHookForm
                        formControl={methods}
                        data={[
                            {
                                renderEditComponent(control) {
                                    return (
                                        <EmployeeSelect
                                            label="employee_id"
                                            control={control}
                                            name={'employee_id'}
                                            multiple
                                            showLabel
                                        />
                                    )
                                },
                                className: 'col-span-4',
                            },
                            {
                                formItemProps: {
                                    label: t('default_rates.rate_of_newsale'),
                                    name: 'default_rates.rate_of_newsale',
                                    type: 'number',
                                },
                                className: 'col-span-1',
                            },
                            {
                                formItemProps: {
                                    label: 'default_rates.rate_of_upsale',
                                    name: 'default_rates.rate_of_upsale',
                                    type: 'number',
                                },
                                className: 'col-span-1',
                            },
                            {
                                formItemProps: {
                                    label: 'default_rates.rate_of_renewal',
                                    name: 'default_rates.rate_of_renewal',
                                    type: 'number',
                                },
                                className: 'col-span-1',
                            },
                            {
                                formItemProps: {
                                    label: 'effective_date',
                                    name: 'effective_date',
                                    type: 'datepicker',
                                },
                                className: 'col-span-2',
                            },
                            {
                                formItemProps: {
                                    label: 'expired_date',
                                    name: 'expired_date',
                                    type: 'datepicker',
                                },
                                className: 'col-span-2',
                            },
                            {
                                renderEditComponent() {
                                    return (
                                        <div>
                                            <Typogaphy className="font-medium text-primary mb-4">
                                                {t(
                                                    'kpi.employee.set-rate-for-product-group'
                                                )}
                                            </Typogaphy>
                                            <div className="grid grid-cols-2 gap-4">
                                                {productGroups?.results?.map(
                                                    (group) => (
                                                        <div key={group}>
                                                            <Typogaphy className="font-medium text-xs mb-2 text-primary">
                                                                {group}
                                                            </Typogaphy>
                                                            <div className="grid grid-cols-3 gap-2">
                                                                <FormItem
                                                                    label={t(
                                                                        'kpi.employee.rate_of_newsale'
                                                                    )}
                                                                    type="number"
                                                                    name={`product_group_rates.${group}.rate_of_newsale`}
                                                                    control={
                                                                        methods.control
                                                                    }
                                                                />
                                                                <FormItem
                                                                    label={t(
                                                                        'kpi.employee.rate_of_upsale'
                                                                    )}
                                                                    type="number"
                                                                    name={`product_group_rates.${group}.rate_of_upsale`}
                                                                    control={
                                                                        methods.control
                                                                    }
                                                                />
                                                                <FormItem
                                                                    label={t(
                                                                        'kpi.employee.rate_of_renewal'
                                                                    )}
                                                                    type="number"
                                                                    name={`product_group_rates.${group}.rate_of_renewal`}
                                                                    control={
                                                                        methods.control
                                                                    }
                                                                />
                                                            </div>
                                                        </div>
                                                    )
                                                )}
                                            </div>
                                        </div>
                                    )
                                },
                                className: 'col-span-4',
                            },
                        ]}
                        actions={[
                            {
                                title: t('cancel'),
                                type: 'button',
                                disabled: isPending,
                                handler: () => {
                                    navigate(paths.kpiManage)
                                },
                                variant: 'outlined',
                            },
                            {
                                title: t('save'),
                                type: 'button',
                                loading: isPending,
                                handler: () => {
                                    methods.handleSubmit(onSubmit)()
                                },
                                variant: 'contained',
                                startIcon: <AddIcons />,
                                disabled: !methods.formState.isValid,
                            },
                        ]}
                    />
                </form>
            </Paper>
        </div>
    )
}

export default Create
